import React, { useState, useEffect } from 'react';
import api from '../services/api';

const StorageStateReport = () => {
    const [timestamp, setTimestamp] = useState('');
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [deviceIdToDeviceName, setDeviceIdToDeviceName] = useState({});
    const [userIdToUsername, setUserIdToUsername] = useState({});

    useEffect(() => {
        // Funkcja do pobrania urządzeń i użytkowników
        const fetchDevicesAndUsers = async () => {
            try {
                // Pobieramy urządzenia
                const devicesResponse = await api.get('/devices/');
                const devices = devicesResponse.data;

                const deviceNameMap = {};
                devices.forEach((device) => {
                    deviceNameMap[device.id] = device.name;
                });

                setDeviceIdToDeviceName(deviceNameMap);

                // Pobieramy użytkowników
                const usersResponse = await api.get('/users/');
                const users = usersResponse.data;

                const userNameMap = {};
                users.forEach((user) => {
                    userNameMap[user.id] = user.username;
                });

                setUserIdToUsername(userNameMap);
            } catch (error) {
                console.error('Error fetching devices or users', error);
            }
        };

        fetchDevicesAndUsers();
    }, []);

    const formatDateToBackend = (date) => {
        const pad = (num) => String(num).padStart(2, '0');
        return (
            date.getFullYear() +
            '-' +
            pad(date.getMonth() + 1) +
            '-' +
            pad(date.getDate()) +
            'T' +
            pad(date.getHours()) +
            ':' +
            pad(0) +
            ':' +
            pad(0)
        );
    };

    const handleTimestampChange = (e) => {
        let value = e.target.value;
        if (value) {
            // Zerujemy minuty i sekundy
            const [datePart, timePart] = value.split('T');
            const [hour] = timePart.split(':');
            const adjustedValue = `${datePart}T${hour}:00`;
            setTimestamp(adjustedValue);
        } else {
            setTimestamp(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setReportData(null);
        setError(null);

        if (!timestamp) {
            setError('Proszę podać znacznik czasu.');
            setLoading(false);
            return;
        }

        try {
            const timestampDate = new Date(timestamp);
            // Zerujemy minuty i sekundy
            timestampDate.setMinutes(0, 0, 0);
            const formattedTimestamp = formatDateToBackend(timestampDate);

            const response = await api.get('/reports/storage-state', {
                params: {
                    timestamp: formattedTimestamp,
                },
            });
            setReportData(response.data);
        } catch (error) {
            setError(
                error.response?.data?.detail || 'Błąd podczas pobierania danych raportu'
            );
        } finally {
            setLoading(false);
        }
    };

    // Renderowanie karty dla widoku mobilnego
    const renderMobileDeviceShares = () => {
        return Object.entries(reportData.device_shares).map(([deviceId, share], index) => (
            <div key={deviceId} className="mb-3 p-3 border border-gray-200 rounded">
                <div className="font-semibold">{deviceIdToDeviceName[deviceId] || 'Nieznane PPE'}</div>
                <div>Udział: {share}</div>
            </div>
        ));
    };

    const renderMobilePendingEntries = () => {
        return reportData.pending_entries.length > 0 ? (
            reportData.pending_entries.map((entry, index) => {
                const deviceName = deviceIdToDeviceName[entry.device_id] || 'Nieznane PPE';
                const username = userIdToUsername[entry.user_id] || 'Nieznany użytkownik';
                
                return (
                    <div key={index} className="mb-3 p-3 border border-gray-200 rounded">
                        <div className="font-semibold">{deviceName}</div>
                        <div>Użytkownik: {username}</div>
                        <div>Ilość: {entry.amount}</div>
                    </div>
                );
            })
        ) : (
            <div className="text-center p-3">Brak oczekujących wpisów.</div>
        );
    };

    const renderMobileExpirationEntries = () => {
        return Object.keys(reportData.expiration_entries).length > 0 ? (
            Object.entries(reportData.expiration_entries).map(([date, amount], index) => (
                <div key={index} className="mb-3 p-3 border border-gray-200 rounded">
                    <div className="font-semibold">{new Date(date).toLocaleDateString()}</div>
                    <div>Ilość: {amount}</div>
                </div>
            ))
        ) : (
            <div className="text-center p-3">Brak wygasających wpisów.</div>
        );
    };

    // Renderowanie dla wersji mobilnej
    const renderMobileStorageState = () => (
        <div className="space-y-6">
            <div className="space-y-2">
                <p className="text-sm">
                    <strong>Znacznik czasu:</strong>{' '}
                    {new Date(reportData.timestamp).toLocaleString()}
                </p>
                <p className="text-sm">
                    <strong>Całkowita dostępna ilość:</strong>{' '}
                    {reportData.total_available_amount}
                </p>
                <p className="text-sm">
                    <strong>Dostępna ilość użytkownika:</strong>{' '}
                    {reportData.user_available_amount}
                </p>
            </div>

            {/* Sekcja Udziały PPE */}
            <div>
                <h4 className="text-lg font-bold mb-2">Udziały PPE:</h4>
                {reportData.device_shares && Object.keys(reportData.device_shares).length > 0 ? (
                    renderMobileDeviceShares()
                ) : (
                    <div className="text-center p-3">Brak udziałów PPE.</div>
                )}
            </div>

            {/* Sekcja Oczekujące wpisy */}
            <div>
                <h4 className="text-lg font-bold mb-2">Oczekujące wpisy:</h4>
                {renderMobilePendingEntries()}
            </div>

            {/* Sekcja Wpisy wygasające */}
            <div>
                <h4 className="text-lg font-bold mb-2">Wpisy wygasające:</h4>
                {renderMobileExpirationEntries()}
            </div>
        </div>
    );

    // Renderowanie tradycyjnych tabel dla większych ekranów
    const renderDesktopStorageState = () => (
        <div>
            <p>
                <strong>Znacznik czasu:</strong>{' '}
                {new Date(reportData.timestamp).toLocaleString()}
            </p>
            <p>
                <strong>Całkowita dostępna ilość:</strong>{' '}
                {reportData.total_available_amount}
            </p>
            <p>
                <strong>Dostępna ilość użytkownika:</strong>{' '}
                {reportData.user_available_amount}
            </p>

            {/* Sekcja Udziały PPE */}
            <h4 className="text-lg font-bold mt-4">Udziały PPE:</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                    <tr>
                        <th className="border px-4 py-2">Nazwa PPE</th>
                        <th className="border px-4 py-2">Udział</th>
                    </tr>
                    </thead>
                    <tbody>
                    {reportData.device_shares &&
                        Object.entries(reportData.device_shares).map(
                            ([deviceId, share]) => (
                                <tr key={deviceId}>
                                    <td className="border px-4 py-2">
                                        {deviceIdToDeviceName[deviceId] || 'Nieznane PPE'}
                                    </td>
                                    <td className="border px-4 py-2">{share}</td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>

            {/* Sekcja Oczekujące wpisy */}
            <h4 className="text-lg font-bold mt-4">Oczekujące wpisy:</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                    <tr>
                        <th className="border px-4 py-2">Nazwa PPE</th>
                        <th className="border px-4 py-2">Nazwa użytkownika</th>
                        <th className="border px-4 py-2">Ilość</th>
                    </tr>
                    </thead>
                    <tbody>
                    {reportData.pending_entries && reportData.pending_entries.length > 0 ? (
                        reportData.pending_entries.map((entry, index) => {
                            const deviceName =
                                deviceIdToDeviceName[entry.device_id] || 'Nieznane PPE';
                            const username =
                                userIdToUsername[entry.user_id] || 'Nieznany użytkownik';

                            return (
                                <tr key={index}>
                                    <td className="border px-4 py-2">{deviceName}</td>
                                    <td className="border px-4 py-2">{username}</td>
                                    <td className="border px-4 py-2">{entry.amount}</td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                className="border px-4 py-2 text-center"
                                colSpan="3"
                            >
                                Brak oczekujących wpisów.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Sekcja Wpisy wygasające */}
            <h4 className="text-lg font-bold mt-4">Wpisy wygasające:</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                    <tr>
                        <th className="border px-4 py-2">Data wygaśnięcia</th>
                        <th className="border px-4 py-2">Ilość</th>
                    </tr>
                    </thead>
                    <tbody>
                    {reportData.expiration_entries && Object.keys(reportData.expiration_entries).length > 0 ? (
                        Object.entries(reportData.expiration_entries).map(([date, amount], index) => (
                            <tr key={index}>
                                <td className="border px-4 py-2">{new Date(date).toLocaleDateString()}</td>
                                <td className="border px-4 py-2">{amount}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                className="border px-4 py-2 text-center"
                                colSpan="2"
                            >
                                Brak wygasających wpisów.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col items-center p-4 sm:p-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-full w-full">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Stan magazynu</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="timestamp" className="block text-gray-700 mb-2">
                            Znacznik czasu:
                        </label>
                        <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                            <input
                                type="date"
                                id="date-part"
                                value={timestamp ? timestamp.split('T')[0] : ''}
                                onChange={(e) => {
                                    const datePart = e.target.value;
                                    const timePart = timestamp ? timestamp.split('T')[1] || '00:00' : '00:00';
                                    setTimestamp(`${datePart}T${timePart}`);
                                }}
                                className="w-full sm:w-auto p-2 border border-gray-300 rounded-lg"
                                required
                            />
                            <select
                                id="hour-part"
                                value={timestamp ? timestamp.split('T')[1]?.split(':')[0] || '00' : '00'}
                                onChange={(e) => {
                                    const datePart = timestamp ? timestamp.split('T')[0] : new Date().toISOString().split('T')[0];
                                    setTimestamp(`${datePart}T${e.target.value}:00`);
                                }}
                                className="w-full sm:w-auto p-2 border border-gray-300 rounded-lg"
                                required
                            >
                                {Array.from({ length: 24 }, (_, i) => {
                                    const hour = String(i).padStart(2, '0');
                                    return (
                                        <option key={hour} value={hour}>
                                            {hour}:00
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition"
                        disabled={loading}
                    >
                        {loading ? 'Generowanie raportu...' : 'Generuj raport'}
                    </button>
                </form>

                {loading && <p className="text-center py-4 mt-4">Generowanie raportu...</p>}

                {error && <p className="text-red-500 mt-4">{error}</p>}

                {reportData && !loading && !error && (
                    <div className="mt-6 sm:mt-8">
                        <h3 className="text-lg sm:text-xl font-bold mb-4">Wyniki raportu</h3>
                        
                        <div className="md:hidden">
                            {renderMobileStorageState()}
                        </div>
                        
                        <div className="hidden md:block">
                            {renderDesktopStorageState()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StorageStateReport;
